import React, { useState, useEffect, useMemo } from 'react';
import {
  Container,
  Box,
  Typography,
  Paper,
  ButtonBase,
  Chip,
  Button,
  TextField,
  IconButton,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Checkbox,
  ListItemText,
  Switch,
  FormControlLabel,
  Tabs,
  Tab,
} from '@mui/material';
import InfoTooltip from '../components/common/InfoTooltip';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StarIcon from '@mui/icons-material/Star';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import api from '../services/api';
import {
  RELEVANCE_LEVELS,
  RELEVANCE_LABELS,
  RELEVANCE_COLORS,
  SEVERITY_LEVELS,
  SEVERITY_LABELS,
  SEVERITY_COLORS,
  WORKFLOW_STATUSES,
  WORKFLOW_LABELS,
  WORKFLOW_COLORS,
  CATEGORY_LABELS,
  CATEGORY_OPTIONS,
  HISTORY_EVENT_LABELS,
  HISTORY_FIELD_LABELS,
  labelFor,
} from '../constants/wbNews';

const ITEMS_PAGE_SIZE = 50;

// important/requiresAction/unread are plain "only show X" checkboxes in this UI (the backend
// itself supports full tri-state true/false/absent - see buildListParams, which only ever
// sends `true` from here, and simply omits the param otherwise so "off" means "no filter", not
// "false"). severity (renamed "Критичность" in the UI) is the news item's own criticality
// classification - a completely different concept from the is_important flag below, which is
// an admin's manual marking; the two must never be merged into one filter.
const emptyFilters = {
  search: '', relevance: [], category: [], severity: [], workflow: [], dateFrom: '', dateTo: '',
  important: false, requiresAction: false, unread: false,
};

const formatDateTime = (value) => (value ? new Date(value).toLocaleString('ru-RU') : '');
const formatDate = (value) => (value ? new Date(value).toLocaleDateString('ru-RU') : '');

const truncate = (value, max = 100) => {
  if (typeof value !== 'string') return value;
  return value.length > max ? `${value.slice(0, max)}…` : value;
};

// old_value/new_value are free-form JSON from the backend (models.JSONB) - merge their keys
// so a field that only appears on one side (e.g. a brand-new field on the first history entry)
// still renders.
const diffValues = (oldValue, newValue) => {
  const keys = new Set([...Object.keys(oldValue || {}), ...Object.keys(newValue || {})]);
  return [...keys].map((key) => [key, oldValue?.[key], newValue?.[key]]);
};

const buildListParams = (filters, limit, offset) => {
  const params = { limit, offset };
  if (filters.search.trim()) params.search = filters.search.trim();
  if (filters.relevance.length) params.relevance_level = filters.relevance.join(',');
  if (filters.category.length) params.category = filters.category.join(',');
  if (filters.severity.length) params.severity = filters.severity.join(',');
  if (filters.workflow.length) params.workflow_status = filters.workflow.join(',');
  if (filters.dateFrom) params.date_from = filters.dateFrom;
  if (filters.dateTo) params.date_to = filters.dateTo;
  if (filters.important) params.is_important = 'true';
  if (filters.requiresAction) params.requires_action = 'true';
  if (filters.unread) params.unread = 'true';
  return params;
};

// ─── Small display helpers ──────────────────────────────────────────────────

const TechRow = ({ label, value }) => (
  <Box>
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{label}</Typography>
    <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>{value || value === 0 ? value : '—'}</Typography>
  </Box>
);

const SummaryCard = ({ label, value, color }) => (
  <Paper sx={{ p: 2, borderRadius: '16px', border: '1px solid rgba(25,198,192,0.18)' }}>
    <Typography color="text.secondary" variant="body2">{label}</Typography>
    <Typography sx={{ fontSize: '2rem', fontWeight: 700, color, my: 0.5 }}>{value}</Typography>
  </Paper>
);

const API_STATE = {
  up: { label: 'Работает', color: 'success' },
  down: { label: 'Не отвечает', color: 'error' },
  unknown: { label: 'Нет данных', color: 'default' },
};

// One WB API direction as Marqit's own /ping check sees it - not WB news, so it lives here and
// never in the journal below.
const APIStatusRow = ({ status }) => {
  const state = API_STATE[status.state] || API_STATE.unknown;
  return (
    <Box
      sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1, flexWrap: 'wrap' }}
    >
      <Chip size="small" color={state.color} label={state.label} sx={{ minWidth: 104 }} />
      <Box sx={{ flex: 1, minWidth: 180 }}>
        <Typography variant="body2" fontWeight={600}>{status.name}</Typography>
        <Typography variant="caption" color="text.secondary">
          {status.reason}
          {status.state === 'down' && status.since ? ` · с ${formatDateTime(status.since)}` : ''}
        </Typography>
      </Box>
    </Box>
  );
};

const SourceCard = ({ source, onRun, running }) => {
  const runnable = source.enabled && source.fetcher_implemented;
  return (
    <Paper sx={{ p: 2, borderRadius: '16px', border: '1px solid rgba(25,198,192,0.15)' }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" gap={1} flexWrap="wrap">
        <Typography variant="subtitle1" fontWeight={700}>{source.display_name}</Typography>
        <Chip size="small" label={source.enabled ? 'Включён' : 'Отключён'} color={source.enabled ? 'success' : 'default'} />
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
        Последняя успешная проверка: {source.last_success_at ? formatDateTime(source.last_success_at) : 'ещё не было'}
      </Typography>
      {source.last_error && (
        <Typography variant="body2" color="error" sx={{ mt: 0.5 }}>
          Ошибка: {source.last_error}{source.last_error_at ? ` (${formatDateTime(source.last_error_at)})` : ''}
        </Typography>
      )}
      {source.warnings?.length > 0 && (
        <Box sx={{ mt: 1 }}>
          {source.warnings.map((w) => (
            <Alert key={w} severity="warning" sx={{ mt: 0.5 }}>{w}</Alert>
          ))}
        </Box>
      )}
      {source.last_run && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          Последний запуск: {formatDateTime(source.last_run.started_at)} ·{' '}
          {source.last_run.succeeded == null ? 'выполняется' : source.last_run.succeeded ? 'успешно' : 'с ошибкой'} ·
          {' '}получено {source.last_run.fetched_count}, новых {source.last_run.new_count}
        </Typography>
      )}
      <Box sx={{ mt: 1.5 }}>
        {runnable ? (
          <Button
            size="small"
            variant="outlined"
            startIcon={<PlayArrowIcon />}
            disabled={running}
            onClick={() => onRun(source)}
            aria-label={`Проверить источник ${source.display_name}`}
          >
            {running ? 'Проверка…' : 'Проверить сейчас'}
          </Button>
        ) : (
          <Typography variant="caption" color="text.secondary">
            {source.fetcher_implemented ? 'Источник отключён' : 'Источник недоступен'}
          </Typography>
        )}
      </Box>
    </Paper>
  );
};

// Urgency as the admin reads it. Computed by the backend from the news text (see newsfeed.Explain).
const URGENCY = {
  now: { label: 'Срочно', color: '#D94F5C', bg: 'rgba(217,79,92,0.10)', hint: 'Может сломать загрузку данных или цифры Marqit. Нужно проверить сегодня.' },
  soon: { label: 'Скоро', color: '#B26A00', bg: 'rgba(178,106,0,0.10)', hint: 'Есть сроки или изменения, которые стоит прочитать в ближайшие дни.' },
  fyi: { label: 'К сведению', color: '#5B6B6A', bg: 'rgba(91,107,106,0.08)', hint: 'Срочных действий нет, достаточно знать.' },
};
const URGENCY_ORDER = { now: 0, soon: 1, fyi: 2 };

const LEGEND = {
  title: 'Как читать ленту новостей',
  details: [
    { h: 'Две ленты', t: '«Влияет на работу Marqit»: новости про отчёты, API, комиссии, тарифы, хранение и сбои WB. Они могут изменить цифры или загрузку данных. «Для селлеров»: правила, акции, работа складов и другое, что важно для торговли на WB, но не меняет работу сайта.' },
    { h: 'Срочность', t: '«Срочно»: может сломать загрузку или цифры, проверьте сегодня. «Скоро»: есть сроки или изменения, прочитайте в ближайшие дни. «К сведению»: срочных действий нет. Хорошие новости («работа возобновлена») всегда «К сведению».' },
    { h: 'Блоки в карточке', t: '«Что это значит» и «Что делать»: короткое объяснение простыми словами. «Почему так оценено»: слова и правила, на которых оценка основана, чтобы вы могли её проверить. Текст самой новости WB не меняется и виден в карточке и в подробностях.' },
    { h: 'Важно знать', t: 'Оценка сейчас делается по правилам (ключевым словам), без ИИ, поэтому иногда может ошибаться. Если оценка кажется неверной, откройте новость и нажмите «Переклассифицировать» или отметьте её вручную.' },
  ],
};

const ItemCard = ({ item, sourceName, onOpen }) => {
  const u = URGENCY[item.urgency] || URGENCY.fyi;
  return (
    <ButtonBase
      onClick={() => onOpen(item)}
      aria-label={`Открыть новость: ${item.title}`}
      sx={{ display: 'block', width: '100%', textAlign: 'left' }}
    >
      <Paper
        sx={{
          p: 2,
          width: '100%',
          borderRadius: '16px',
          border: '1px solid rgba(25,198,192,0.15)',
          borderLeft: `5px solid ${u.color}`,
          transition: 'box-shadow 0.15s',
          '&:hover': { boxShadow: '0 4px 16px rgba(25,198,192,0.18)' },
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1} flexWrap="wrap">
          <Box display="flex" gap={0.75} alignItems="center" flexWrap="wrap">
            <Chip size="small" label={u.label} sx={{ bgcolor: u.bg, color: u.color, fontWeight: 700 }} />
            <Chip size="small" variant="outlined" label={CATEGORY_LABELS[item.category] || item.category} />
            {!item.read_at && <Chip size="small" label="Новая" color="info" />}
            {item.is_important && <Chip size="small" icon={<StarIcon fontSize="small" />} label="Важно" color="warning" />}
            {item.requires_action && <Chip size="small" icon={<PriorityHighIcon fontSize="small" />} label="Требует действия" color="error" />}
          </Box>
          <Typography variant="caption" color="text.secondary">{formatDate(item.published_at || item.first_seen_at)}</Typography>
        </Box>
        <Typography variant="subtitle1" fontWeight={700} sx={{ mt: 1 }}>{item.title}</Typography>
        <Box sx={{ mt: 1, display: 'grid', gap: 0.75 }}>
          <Typography variant="body2"><b>Что это значит:</b> {item.plain_meaning}</Typography>
          <Typography variant="body2"><b>Что делать:</b> {item.plain_action}</Typography>
          <Typography variant="body2" color="text.secondary" component="div">
            <b>Почему так оценено:</b> {item.plain_why}
            {item.matched_words?.length > 0 && <> Найдено в тексте: {item.matched_words.map((w) => `«${w}»`).join(', ')}.</>}
          </Typography>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          {sourceName || item.source_key} · нажмите, чтобы открыть текст новости WB
        </Typography>
      </Paper>
    </ButtonBase>
  );
};

const HistoryEntry = ({ entry }) => {
  const label = HISTORY_EVENT_LABELS[entry.event_type] || entry.event_type;
  const changes = diffValues(entry.old_value, entry.new_value);
  return (
    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: '12px' }}>
      <Box display="flex" justifyContent="space-between" flexWrap="wrap" gap={1}>
        <Typography variant="body2" fontWeight={600}>{label}</Typography>
        <Typography variant="caption" color="text.secondary">{formatDateTime(entry.created_at)}</Typography>
      </Box>
      {changes.length > 0 && (
        <Box sx={{ mt: 0.5 }}>
          {changes.map(([key, from, to]) => (
            <Typography key={key} variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {labelFor(HISTORY_FIELD_LABELS, key)}: {String(truncate(from) ?? '—')} → {String(truncate(to) ?? '—')}
            </Typography>
          ))}
        </Box>
      )}
      {entry.comment && <Typography variant="body2" sx={{ mt: 0.5 }}>«{entry.comment}»</Typography>}
    </Paper>
  );
};

// ─── Page ────────────────────────────────────────────────────────────────────

const AdminWBNewsPage = () => {
  const [summary, setSummary] = useState(null);
  const [summaryError, setSummaryError] = useState('');

  const [sources, setSources] = useState([]);
  const [sourcesLoading, setSourcesLoading] = useState(true);
  const [sourcesError, setSourcesError] = useState('');
  const [apiStatus, setApiStatus] = useState(null);
  const [apiStatusError, setApiStatusError] = useState('');
  const [runningKey, setRunningKey] = useState(null);

  const [filters, setFilters] = useState(emptyFilters);
  const [searchDraft, setSearchDraft] = useState('');

  const [items, setItems] = useState([]);
  const [itemsLoading, setItemsLoading] = useState(true);
  const [itemsError, setItemsError] = useState('');
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [offset, setOffset] = useState(0);

  const [group, setGroup] = useState('marqit');
  const [notice, setNotice] = useState(null);

  const [detailOpen, setDetailOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');
  const [commentDraft, setCommentDraft] = useState('');
  const [actionBusy, setActionBusy] = useState(false);
  const [reclassifyBusy, setReclassifyBusy] = useState(false);

  const sourceNameByKey = useMemo(
    () => Object.fromEntries(sources.map((s) => [s.source_key, s.display_name])),
    [sources]
  );

  const loadSummary = async () => {
    try {
      const res = await api.get('/admin/wb-news/summary');
      setSummary(res.data);
      setSummaryError('');
    } catch (err) {
      setSummaryError('Не удалось загрузить сводку. Попробуйте обновить страницу.');
    }
  };

  const loadAPIStatus = async () => {
    try {
      const res = await api.get('/admin/wb-news/api-status');
      setApiStatus(res.data || []);
      setApiStatusError('');
    } catch (err) {
      setApiStatusError('Не удалось загрузить доступность API WB.');
    }
  };

  const loadSources = async () => {
    try {
      const res = await api.get('/admin/wb-news/sources');
      setSources(res.data || []);
      setSourcesError('');
    } catch (err) {
      setSourcesError('Не удалось загрузить состояние источников.');
    } finally {
      setSourcesLoading(false);
    }
  };

  const loadItems = async (reset) => {
    const nextOffset = reset ? 0 : offset;
    if (reset) {
      setItemsLoading(true);
      setItemsError('');
    } else {
      setLoadingMore(true);
    }
    try {
      const params = buildListParams(filters, ITEMS_PAGE_SIZE, nextOffset);
      const res = await api.get('/admin/wb-news/items', { params });
      const data = res.data || [];
      setItems((prev) => (reset ? data : [...prev, ...data]));
      setHasMore(data.length === ITEMS_PAGE_SIZE);
      setOffset(nextOffset + data.length);
      if (reset) setItemsError('');
    } catch (err) {
      setItemsError('Не удалось загрузить список новостей. Попробуйте обновить страницу.');
      if (reset) setItems([]);
    } finally {
      setItemsLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    // Bring older items to the current rules (items already up to date are skipped), then reload the list.
    api.post('/admin/wb-news/reclassify').then((res) => { if ((res.data?.changed ?? 0) > 0) { loadItems(true); loadSummary(); } }).catch(() => {});
    loadSummary();
    loadSources();
    loadAPIStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadItems(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const applySearch = () => setFilters((prev) => ({ ...prev, search: searchDraft }));

  const resetFilters = () => {
    setSearchDraft('');
    setFilters(emptyFilters);
  };

  const hasActiveFilters =
    !!filters.search || filters.relevance.length > 0 || filters.category.length > 0 ||
    filters.severity.length > 0 || filters.workflow.length > 0 || !!filters.dateFrom || !!filters.dateTo ||
    filters.important || filters.requiresAction || filters.unread;

  const applyItemFields = (updated) => {
    setItems((prev) => prev.map((it) => (it.id === updated.id ? { ...it, ...updated } : it)));
  };

  // Re-fetches the full detail (item fields + history) for one item and applies it to both the
  // open detail dialog and the matching card in the list - the one place all post-action
  // refreshes go through, so a new history entry always shows up in an already-open dialog
  // without closing/reopening it, and the card/summary never drift from what the dialog shows.
  const refreshItemAndDetail = async (itemId) => {
    const detailRes = await api.get(`/admin/wb-news/items/${itemId}`);
    setDetail(detailRes.data);
    const { history, ...itemFields } = detailRes.data;
    applyItemFields(itemFields);
  };

  const runItemAction = async (item, fn, successMessage) => {
    setActionBusy(true);
    try {
      await fn();
      await refreshItemAndDetail(item.id);
      await loadSummary();
      setNotice({ severity: 'success', message: successMessage });
    } catch (err) {
      setNotice({ severity: 'error', message: err.response?.data?.error || 'Не удалось выполнить действие. Попробуйте ещё раз.' });
    } finally {
      setActionBusy(false);
    }
  };

  const handleMarkRead = (item) =>
    runItemAction(item, () => api.patch(`/admin/wb-news/items/${item.id}/read`), 'Отмечено прочитанным.');

  const handleToggleImportant = (item) =>
    runItemAction(
      item,
      () => api.patch(`/admin/wb-news/items/${item.id}/important`, { is_important: !item.is_important }),
      !item.is_important ? 'Отмечено важным.' : 'Отметка важности снята.'
    );

  const handleToggleRequiresAction = (item) =>
    runItemAction(
      item,
      () => api.patch(`/admin/wb-news/items/${item.id}/requires-action`, { requires_action: !item.requires_action }),
      !item.requires_action ? 'Отмечено «требует действия».' : 'Признак «требует действия» снят.'
    );

  const handleWorkflowChange = (item, workflow_status) =>
    runItemAction(
      item,
      () => api.patch(`/admin/wb-news/items/${item.id}/workflow-status`, { workflow_status }),
      'Статус обработки обновлён.'
    );

  const handleSaveComment = (item, comment) =>
    runItemAction(item, () => api.patch(`/admin/wb-news/items/${item.id}/comment`, { comment }), 'Комментарий сохранён.');

  const handleReclassify = async (item) => {
    setReclassifyBusy(true);
    try {
      const res = await api.post('/admin/wb-news/reclassify', null, { params: { item_id: item.id } });
      const changed = res.data?.changed ?? 0;
      await refreshItemAndDetail(item.id);
      await loadSummary();
      setNotice({
        severity: changed > 0 ? 'success' : 'info',
        message: changed > 0 ? 'Классификация обновлена.' : 'Классификация не изменилась.',
      });
    } catch (err) {
      setNotice({ severity: 'error', message: err.response?.data?.error || 'Не удалось выполнить переклассификацию.' });
    } finally {
      setReclassifyBusy(false);
    }
  };

  const openDetail = async (item) => {
    setDetailOpen(true);
    setDetail(null);
    setDetailError('');
    setDetailLoading(true);
    try {
      const res = await api.get(`/admin/wb-news/items/${item.id}`);
      setDetail(res.data);
      setCommentDraft(res.data.admin_comment || '');
    } catch (err) {
      setDetailError('Не удалось загрузить подробности новости.');
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setDetailOpen(false);
    setDetail(null);
    setDetailError('');
  };

  const handleRunSource = async (source) => {
    setRunningKey(source.source_key);
    try {
      await api.post(`/admin/wb-news/sources/${source.source_key}/run`);
      setNotice({ severity: 'success', message: `Проверка источника «${source.display_name}» завершена.` });
      await Promise.all([loadSources(), loadSummary(), loadItems(true), loadAPIStatus()]);
    } catch (err) {
      setNotice({ severity: 'error', message: err.response?.data?.error || 'Не удалось выполнить проверку источника.' });
    } finally {
      setRunningKey(null);
    }
  };

  const grouped = useMemo(() => {
    const sorted = [...items].sort((a, b) => (URGENCY_ORDER[a.urgency] ?? 2) - (URGENCY_ORDER[b.urgency] ?? 2)
      || new Date(b.published_at || b.first_seen_at) - new Date(a.published_at || a.first_seen_at));
    return { marqit: sorted.filter((i) => i.impact_group === 'marqit'), sellers: sorted.filter((i) => i.impact_group !== 'marqit') };
  }, [items]);

  const summaryCards = summary
    ? [
        { label: 'Всего', value: summary.total_count, color: '#19C6C0' },
        { label: 'Непрочитанные', value: summary.unread_count, color: '#2F80ED' },
        { label: 'Критические', value: summary.critical_count, color: '#D94F5C' },
        { label: 'Требуют действий', value: summary.requires_action_count, color: '#B26A00' },
        { label: 'В работе', value: summary.in_progress_count, color: '#8A63D2' },
      ]
    : [];

  const multiSelectProps = (value, onChange, options, labels, label) => ({
    size: 'small',
    sx: { minWidth: 190 },
    children: (
      <>
        <InputLabel>{label}</InputLabel>
        <Select
          multiple
          value={value}
          onChange={(e) => onChange(e.target.value)}
          input={<OutlinedInput label={label} />}
          renderValue={(selected) => (selected.length ? selected.map((v) => labels[v] || v).join(', ') : 'По умолчанию')}
        >
          {options.map((opt) => (
            <MenuItem key={opt} value={opt}>
              <Checkbox checked={value.includes(opt)} size="small" />
              <ListItemText primary={labels[opt] || opt} />
            </MenuItem>
          ))}
        </Select>
      </>
    ),
  });

  return (
    <Container maxWidth={false}>
      <Box mb={3}>
        <Typography variant="h4">Мониторинг новостей Wildberries</Typography>
        <Typography color="text.secondary" sx={{ mt: 1 }}>
          Журнал новостей самого Wildberries: изменения отчётов, API, правил и тарифов. Проверки
          доступности API, которые делает Marqit, показаны отдельным блоком и в журнал не попадают.
        </Typography>
      </Box>

      {notice && (
        <Alert severity={notice.severity} onClose={() => setNotice(null)} sx={{ mb: 2 }}>
          {notice.message}
        </Alert>
      )}

      {summaryError && <Alert severity="error" sx={{ mb: 2 }}>{summaryError}</Alert>}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(3, 1fr)', lg: 'repeat(5, 1fr)' }, gap: 2, mb: 3 }}>
        {summaryCards.map((card) => (
          <SummaryCard key={card.label} {...card} />
        ))}
        {!summary && !summaryError && (
          <Box sx={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        )}
      </Box>

      <Typography variant="h6" sx={{ mb: 1.5 }}>Источники</Typography>
      {sourcesError && <Alert severity="error" sx={{ mb: 2 }}>{sourcesError}</Alert>}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2, mb: 3 }}>
        {sourcesLoading ? (
          <Box sx={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : sources.length === 0 && !sourcesError ? (
          <Paper sx={{ gridColumn: '1 / -1', p: 3, textAlign: 'center', borderRadius: '16px' }}>
            <Typography color="text.secondary">Источники не настроены.</Typography>
          </Paper>
        ) : (
          sources.map((source) => (
            <SourceCard key={source.source_key} source={source} onRun={handleRunSource} running={runningKey === source.source_key} />
          ))
        )}
      </Box>
      <Button
        size="small"
        startIcon={<OpenInNewIcon />}
        component="a"
        href="https://dev.wildberries.ru/release-notes"
        target="_blank"
        rel="noopener noreferrer"
        sx={{ mt: -1.5, mb: 3 }}
      >
        Открыть официальный журнал WB API
      </Button>

      {!sourcesLoading && !sourcesError && !sources.some((src) => src.source_key === 'wb_portal_news_api') && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Новости WB сейчас не загружаются: нет токена. Подключите кабинет Wildberries под
          администратором (или задайте WB_NEWS_API_TOKEN на сервере), и новости появятся в журнале.
        </Alert>
      )}

      <Typography variant="h6" sx={{ mb: 0.5 }}>Доступность API WB</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Marqit каждые 5 минут проверяет, отвечают ли разделы API Wildberries. Если не ответил ни один
        раздел, это считается проблемой сети на нашей стороне, а не сбоем WB.
        {apiStatus?.[0]?.last_checked_at ? ` Последняя проверка: ${formatDateTime(apiStatus[0].last_checked_at)}.` : ''}
      </Typography>
      {apiStatusError && <Alert severity="error" sx={{ mb: 2 }}>{apiStatusError}</Alert>}
      <Paper sx={{ px: 2, py: 0.5, borderRadius: '16px', mb: 3, border: '1px solid rgba(25,198,192,0.15)', display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, columnGap: 3 }}>
        {apiStatus === null && !apiStatusError ? (
          <Box display="flex" justifyContent="center" py={2} sx={{ gridColumn: '1 / -1' }}><CircularProgress size={24} /></Box>
        ) : (
          (apiStatus || []).map((st) => <APIStatusRow key={st.key} status={st} />)
        )}
      </Paper>

      <Box display="flex" alignItems="center" gap={0.75} sx={{ mb: 1.5 }}>
        <Typography variant="h6">Журнал новостей WB</Typography>
        <InfoTooltip exclamation size={20} title="Новости WB, разложенные по влиянию на работу Marqit и срочности." heading={LEGEND.title} details={LEGEND.details} />
      </Box>

      <Paper sx={{ p: 2, borderRadius: '16px', mb: 3 }}>
        <Box display="flex" gap={1.5} flexWrap="wrap" alignItems="center">
          <TextField
            size="small"
            label="Поиск"
            value={searchDraft}
            onChange={(e) => setSearchDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') applySearch();
            }}
            sx={{ minWidth: 200, flex: 1 }}
          />
          <Button size="small" variant="outlined" onClick={applySearch}>Найти</Button>

          <FormControl {...multiSelectProps(
            filters.relevance,
            (v) => setFilters((prev) => ({ ...prev, relevance: v })),
            RELEVANCE_LEVELS,
            RELEVANCE_LABELS,
            'Релевантность'
          )} />
          <FormControl {...multiSelectProps(
            filters.category,
            (v) => setFilters((prev) => ({ ...prev, category: v })),
            CATEGORY_OPTIONS,
            CATEGORY_LABELS,
            'Категория'
          )} />
          <FormControl {...multiSelectProps(
            filters.severity,
            (v) => setFilters((prev) => ({ ...prev, severity: v })),
            SEVERITY_LEVELS,
            SEVERITY_LABELS,
            'Критичность'
          )} />
          <FormControl {...multiSelectProps(
            filters.workflow,
            (v) => setFilters((prev) => ({ ...prev, workflow: v })),
            WORKFLOW_STATUSES,
            WORKFLOW_LABELS,
            'Статус обработки'
          )} />

          <TextField
            size="small"
            type="date"
            label="Публикация с"
            InputLabelProps={{ shrink: true }}
            value={filters.dateFrom}
            onChange={(e) => setFilters((prev) => ({ ...prev, dateFrom: e.target.value }))}
          />
          <TextField
            size="small"
            type="date"
            label="Публикация по"
            InputLabelProps={{ shrink: true }}
            value={filters.dateTo}
            onChange={(e) => setFilters((prev) => ({ ...prev, dateTo: e.target.value }))}
          />

          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={filters.important}
                onChange={(e) => setFilters((prev) => ({ ...prev, important: e.target.checked }))}
              />
            }
            label="Только важные"
          />
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={filters.requiresAction}
                onChange={(e) => setFilters((prev) => ({ ...prev, requiresAction: e.target.checked }))}
              />
            }
            label="Только требующие действия"
          />
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={filters.unread}
                onChange={(e) => setFilters((prev) => ({ ...prev, unread: e.target.checked }))}
              />
            }
            label="Только непрочитанные"
          />

          <Button size="small" onClick={resetFilters} disabled={!hasActiveFilters && !searchDraft}>
            Сбросить фильтры
          </Button>
        </Box>
      </Paper>

      {itemsError && <Alert severity="error" sx={{ mb: 2 }}>{itemsError}</Alert>}

      {itemsLoading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : items.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center', borderRadius: '16px' }}>
          <Typography color="text.secondary">{hasActiveFilters ? 'Новости не найдены.' : 'Новостей WB пока нет.'}</Typography>
          {hasActiveFilters && (
            <Button onClick={resetFilters} sx={{ mt: 1 }}>Сбросить фильтры</Button>
          )}
        </Paper>
      ) : (
        <>
          <Tabs value={group} onChange={(_, v) => setGroup(v)} sx={{ mb: 0.5 }} aria-label="Группа новостей">
            <Tab value="marqit" label={`Влияет на работу Marqit (${grouped.marqit.length})`} />
            <Tab value="sellers" label={`Для селлеров (${grouped.sellers.length})`} />
          </Tabs>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {group === 'marqit'
              ? 'Новости, которые могут изменить цифры или загрузку данных: отчёты, API, комиссии, тарифы, хранение, сбои WB. Сверху самые срочные.'
              : 'Правила, акции, работа складов и другое. Важно для торговли на WB, но на работу сайта не влияет.'}
          </Typography>
          <Box display="flex" flexDirection="column" gap={1.5}>
            {grouped[group].length === 0 && (
              <Paper sx={{ p: 3, textAlign: 'center', borderRadius: '16px' }}>
                <Typography color="text.secondary">В этой ленте новостей пока нет.</Typography>
              </Paper>
            )}
            {grouped[group].map((item) => (
              <ItemCard key={item.id} item={item} sourceName={sourceNameByKey[item.source_key]} onOpen={openDetail} />
            ))}
          </Box>
          {hasMore && (
            <Box display="flex" justifyContent="center" mt={2}>
              <Button variant="outlined" disabled={loadingMore} onClick={() => loadItems(false)}>
                {loadingMore ? 'Загрузка…' : 'Показать ещё'}
              </Button>
            </Box>
          )}
        </>
      )}

      <Dialog open={detailOpen} onClose={closeDetail} maxWidth="md" fullWidth scroll="paper">
        <DialogTitle sx={{ pr: 6 }}>
          {detail?.title || 'Подробности новости'}
          <IconButton onClick={closeDetail} aria-label="Закрыть" sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {detailLoading && (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          )}
          {detailError && <Alert severity="error">{detailError}</Alert>}
          {detail && !detailLoading && (
            <>
              <Box display="flex" gap={0.75} flexWrap="wrap" mb={2}>
                <Chip size="small" color={WORKFLOW_COLORS[detail.workflow_status]} label={WORKFLOW_LABELS[detail.workflow_status] || detail.workflow_status} />
                <Chip size="small" color={SEVERITY_COLORS[detail.severity]} label={SEVERITY_LABELS[detail.severity] || detail.severity} />
                <Chip size="small" variant="outlined" color={RELEVANCE_COLORS[detail.relevance_level]} label={RELEVANCE_LABELS[detail.relevance_level] || detail.relevance_level} />
                <Chip size="small" label={CATEGORY_LABELS[detail.category] || detail.category} />
                {detail.is_important && <Chip size="small" color="warning" icon={<StarIcon fontSize="small" />} label="Важно" />}
                {detail.requires_action && <Chip size="small" color="error" icon={<PriorityHighIcon fontSize="small" />} label="Требует действия" />}
                {!detail.read_at && <Chip size="small" color="info" label="Не прочитано" />}
              </Box>

              <Paper variant="outlined" sx={{ p: 1.5, mb: 2, borderRadius: '12px', display: 'grid', gap: 0.75 }}>
                <Typography variant="body2"><b>Куда относится:</b> {detail.impact_group === 'marqit' ? 'влияет на работу Marqit' : 'для селлеров, на работу Marqit не влияет'}</Typography>
                <Typography variant="body2"><b>Срочность:</b> {(URGENCY[detail.urgency] || URGENCY.fyi).label}. {(URGENCY[detail.urgency] || URGENCY.fyi).hint}</Typography>
                <Typography variant="body2"><b>Что это значит:</b> {detail.plain_meaning}</Typography>
                <Typography variant="body2"><b>Что делать:</b> {detail.plain_action}</Typography>
                <Typography variant="body2" color="text.secondary"><b>Почему так оценено:</b> {detail.plain_why}</Typography>
              </Paper>
              <Typography variant="overline" color="text.secondary">Текст новости WB без изменений</Typography>
              <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', mb: 2 }}>{detail.original_text}</Typography>

              <Button
                size="small"
                startIcon={<OpenInNewIcon />}
                component="a"
                href={detail.canonical_url}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ mb: 2 }}
              >
                Оригинальная публикация
              </Button>

              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Технические данные</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5, mb: 2 }}>
                <TechRow label="Источник" value={sourceNameByKey[detail.source_key] || detail.source_key} />
                <TechRow label="ID публикации" value={detail.external_id} />
                <TechRow label="Версия классификатора" value={detail.classifier_version} />
                <TechRow label="Тип изменения" value={detail.change_type} />
                <TechRow label="Причина релевантности" value={detail.relevance_reason} />
                <TechRow label="Затронутые модули" value={detail.affected_modules?.length ? detail.affected_modules.join(', ') : '—'} />
                <TechRow label="Опубликовано" value={formatDateTime(detail.published_at)} />
                <TechRow label="Впервые обнаружено" value={formatDateTime(detail.first_seen_at)} />
                <TechRow label="Создано" value={formatDateTime(detail.created_at)} />
                <TechRow label="Обновлено" value={formatDateTime(detail.updated_at)} />
              </Box>

              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Действия</Typography>
              <Box display="flex" gap={1.5} flexWrap="wrap" alignItems="center" mb={2}>
                {!detail.read_at ? (
                  <Button size="small" variant="outlined" disabled={actionBusy} onClick={() => handleMarkRead(detail)}>
                    Отметить прочитанной
                  </Button>
                ) : (
                  <Chip size="small" color="success" icon={<CheckCircleIcon fontSize="small" />} label={`Прочитано ${formatDateTime(detail.read_at)}`} />
                )}
                <FormControlLabel
                  control={<Switch checked={detail.is_important} disabled={actionBusy} onChange={() => handleToggleImportant(detail)} />}
                  label="Важно"
                />
                <FormControlLabel
                  control={<Switch checked={detail.requires_action} disabled={actionBusy} onChange={() => handleToggleRequiresAction(detail)} />}
                  label="Требует действия"
                />
                <FormControl size="small" sx={{ minWidth: 170 }}>
                  <InputLabel>Статус обработки</InputLabel>
                  <Select
                    label="Статус обработки"
                    value={detail.workflow_status}
                    disabled={actionBusy}
                    onChange={(e) => handleWorkflowChange(detail, e.target.value)}
                  >
                    {WORKFLOW_STATUSES.map((s) => (
                      <MenuItem key={s} value={s}>{WORKFLOW_LABELS[s]}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button size="small" variant="outlined" disabled={reclassifyBusy} onClick={() => handleReclassify(detail)}>
                  {reclassifyBusy ? 'Переклассификация…' : 'Переклассифицировать'}
                </Button>
              </Box>

              <TextField
                fullWidth
                multiline
                minRows={2}
                label="Комментарий администратора"
                value={commentDraft}
                onChange={(e) => setCommentDraft(e.target.value)}
                sx={{ mb: 1 }}
              />
              <Button
                size="small"
                variant="contained"
                disabled={actionBusy || commentDraft === (detail.admin_comment || '')}
                onClick={() => handleSaveComment(detail, commentDraft)}
              >
                Сохранить комментарий
              </Button>

              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>История изменений</Typography>
              {detail.history?.length ? (
                <Box display="flex" flexDirection="column" gap={1}>
                  {detail.history.map((h) => (
                    <HistoryEntry key={h.id} entry={h} />
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">История пуста.</Typography>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </Container>
  );
};

export default AdminWBNewsPage;
